import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase, Celebration } from '../../lib/supabase';

export default function HomePage() {
  const [allCelebrations, setAllCelebrations] = useState<Celebration[]>([]);
  const [celebrations, setCelebrations] = useState<Celebration[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fadeIn, setFadeIn] = useState(true);
  const [isMuted, setIsMuted] = useState(true);
  const [showArtworkInfo, setShowArtworkInfo] = useState(false);
  const prevIdsRef = useRef<string>('');

  // SEO: Add Schema.org JSON-LD
  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

    const schemaData = [
      {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        'name': '온라인 미술 작품 전시 및 판매 플랫폼',
        'url': siteUrl,
        'description': '미술 작품을 1시간에 1작품만 전시하여 판매할 수 있는 온라인 미술 작품 전시 및 판매 플랫폼입니다. 작가나 컬렉터 등 누구나 작품을 등록하여 저렴한 수수료에 미술품 판매가 가능합니다.',
        'inLanguage': 'ko-KR',
        'potentialAction': {
          '@type': 'SearchAction',
          'target': `${siteUrl}/sell-inquiry?q={search_term_string}`,
          'query-input': 'required name=search_term_string'
        }
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        'name': '온라인 미술 작품 전시 및 판매 플랫폼',
        'url': siteUrl,
        'logo': `${siteUrl}/vite.svg`,
        'description': '미술품 판매, 아트 딜러, 온라인 전시 서비스를 제공하는 온라인 미술 작품 전시 및 판매 플랫폼입니다.',
        'areaServed': {
          '@type': 'Country',
          'name': '대한민국'
        },
        'contactPoint': {
          '@type': 'ContactPoint',
          'contactType': 'customer service',
          'availableLanguage': 'Korean'
        }
      },
      {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        'name': '온라인 미술 작품 전시 및 판매 플랫폼 | 미술품 판매 · 아트 딜러 · 온라인 전시',
        'url': siteUrl,
        'description': '미술 작품을 1시간에 1작품만 전시하여 판매할 수 있는 온라인 미술 작품 전시 및 판매 플랫폼입니다.',
        'inLanguage': 'ko-KR',
        'isPartOf': {
          '@type': 'WebSite',
          'url': siteUrl
        },
        'breadcrumb': {
          '@type': 'BreadcrumbList',
          'itemListElement': [
            {
              '@type': 'ListItem',
              'position': 1,
              'name': '홈',
              'item': siteUrl
            }
          ]
        }
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Service',
        'serviceType': '미술품 전시 판매 플랫폼 서비스',
        'name': '온라인 미술 작품 전시 및 판매',
        'provider': {
          '@type': 'Organization',
          'name': '온라인 미술 작품 전시 및 판매 플랫폼'
        },
        'areaServed': {
          '@type': 'Country',
          'name': '대한민국'
        },
        'description': '회화, 조각, 사진, 판화 등 다양한 장르의 미술 작품을 온라인으로 전시하고 판매하는 프리미엄 아트 갤러리 플랫폼입니다.',
        'offers': {
          '@type': 'Offer',
          'availability': 'https://schema.org/InStock',
          'priceCurrency': 'KRW'
        }
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        'mainEntity': [
          {
            '@type': 'Question',
            'name': '미술품 판매는 어떻게 신청하나요?',
            'acceptedAnswer': {
              '@type': 'Answer',
              'text': '작품 판매 문의 페이지에서 작가명, 작품명, 작품 스펙, 희망 판매가 등을 입력하고 작품 이미지를 등록하면 전시 및 판매가 가능합니다. 전문 큐레이터가 작품을 검토한 후 온라인 갤러리에 전시됩니다.'
            }
          },
          {
            '@type': 'Question',
            'name': '어떤 종류의 미술품을 판매할 수 있나요?',
            'acceptedAnswer': {
              '@type': 'Answer',
              'text': '회화, 조각, 사진, 판화, 드로잉, 설치미술 등 다양한 장르의 미술 작품을 판매할 수 있습니다. 원작 작품뿐만 아니라 에디션 작품도 판매 가능합니다.'
            }
          },
          {
            '@type': 'Question',
            'name': '작품 구매는 어떻게 하나요?',
            'acceptedAnswer': {
              '@type': 'Answer',
              'text': '전시 중인 작품의 정보를 확인한 후 구매 문의 버튼을 통해 문의하실 수 있습니다. 작품 가격, 배송 방법, 결제 방식 등에 대해 상담 후 구매가 진행됩니다.'
            }
          },
          {
            '@type': 'Question',
            'name': '아트 딜러를 통한 미술품 거래가 가능한가요?',
            'acceptedAnswer': {
              '@type': 'Answer',
              'text': '네, 아트 딜러 서비스를 통해 미술품 거래를 지원합니다. 전문 큐레이터가 작품 감정 및 거래를 도와드립니다.'
            }
          }
        ]
      }
    ];

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(schemaData);
    script.id = 'home-schema';
    document.head.appendChild(script);

    return () => {
      const existingScript = document.getElementById('home-schema');
      if (existingScript) {
        document.head.removeChild(existingScript);
      }
    };
  }, []);

  // DB에서 전체 활성 콘텐츠를 가져오기 (시간 필터링 없이)
  const fetchCelebrations = async () => {
    try {
      setError(null);
      const { data, error } = await supabase
        .from('celebrations')
        .select('*')
        .eq('is_active', true)
        .not('user_id', 'is', null)
        .order('display_order', { ascending: true });

      if (error) throw error;
      setAllCelebrations(data || []);
    } catch (err: any) {
      console.error('Error fetching celebrations:', err);
      if (err?.message?.includes('Failed to fetch') || err?.message?.includes('fetch')) {
        setError('네트워크 연결에 실패했습니다. 인터넷 연결을 확인해주세요.');
      } else {
        setError('데이터를 불러오는 중 오류가 발생했습니다.');
      }
    } finally {
      setLoading(false);
    }
  };

  // 최초 로드 + 60초마다 DB 갱신
  useEffect(() => {
    fetchCelebrations();
    const refreshInterval = setInterval(fetchCelebrations, 60000);
    return () => clearInterval(refreshInterval);
  }, []);

  // 매초 현재 시간 기준으로 노출 대상 필터링
  useEffect(() => {
    const filterByTime = () => {
      const now = new Date();
      const filtered = allCelebrations.filter((item) => {
        if (!item.display_start_at || !item.display_end_at) {
          return true;
        }
        const startTime = new Date(item.display_start_at);
        const endTime = new Date(item.display_end_at);
        return now >= startTime && now <= endTime;
      });

      // 필터링 결과가 변경되었을 때만 상태 업데이트
      const newIds = filtered.map((c) => c.id).join(',');
      if (newIds !== prevIdsRef.current) {
        prevIdsRef.current = newIds;
        setCelebrations((prev) => {
          // 콘텐츠 목록이 바뀌면 인덱스 보정
          const prevIds = prev.map((c) => c.id);
          const changed = filtered.length !== prev.length || filtered.some((c, i) => c.id !== prevIds[i]);
          if (changed) {
            setCurrentIndex(0);
            setCurrentMediaIndex(0);
            setFadeIn(true);
          }
          return filtered;
        });
      }
    };

    filterByTime(); // 즉시 1회 실행
    const timeCheckInterval = setInterval(filterByTime, 1000);
    return () => clearInterval(timeCheckInterval);
  }, [allCelebrations]);

  const getMediaUrls = useCallback((celebration: Celebration): string[] => {
    if (celebration.media_urls && Array.isArray(celebration.media_urls) && celebration.media_urls.length > 0) {
      return celebration.media_urls;
    }
    if (celebration.media_url) {
      return [celebration.media_url];
    }
    return [];
  }, []);

  useEffect(() => {
    if (celebrations.length === 0) return;

    const currentCelebration = celebrations[currentIndex];
    if (!currentCelebration) return;

    const mediaUrls = getMediaUrls(currentCelebration);
    const currentDuration = currentCelebration.duration || 10000;

    const timer = setTimeout(() => {
      setFadeIn(false);
      setTimeout(() => {
        if (currentCelebration.media_type === 'image' && mediaUrls.length > 1) {
          // 여러 이미지가 있는 경우: 다음 이미지로 이동
          const nextMediaIndex = currentMediaIndex + 1;
          if (nextMediaIndex < mediaUrls.length) {
            setCurrentMediaIndex(nextMediaIndex);
          } else {
            // 모든 이미지를 다 보여줫으면 다음 콘텐츠로
            setCurrentMediaIndex(0);
            setCurrentIndex((current) => (current + 1) % celebrations.length);
          }
        } else {
          // 이미지가 1개이거나 동영상인 경우: 다음 콘텐츠로
          setCurrentMediaIndex(0);
          setCurrentIndex((current) => (current + 1) % celebrations.length);
        }
        setFadeIn(true);
      }, 300);
    }, currentDuration);

    return () => clearTimeout(timer);
  }, [currentIndex, currentMediaIndex, celebrations, getMediaUrls]);

  // Vimeo/YouTube URL을 embed URL로 변환하는 함수
  const getEmbedUrl = (url: string, muted: boolean = true) => {
    const muteParam = muted ? 1 : 0;
    const mutedBool = muted ? 1 : 0;

    const youtubeRegex = /(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;
    const youtubeMatch = url.match(youtubeRegex);
    if (youtubeMatch) {
      const videoId = youtubeMatch[1];
      return `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=${muteParam}&loop=1&playlist=${videoId}&controls=0&modestbranding=1&rel=0&showinfo=0&iv_load_policy=3&disablekb=1&fs=0&autohide=1&cc_load_policy=0&color=white&enablejsapi=1`;
    }
    
    if (url.includes('player.vimeo.com')) {
      const baseUrl = url.split('?')[0];
      return `${baseUrl}?autoplay=1&loop=1&autopause=0&muted=${mutedBool}&title=0&byline=0&portrait=0&controls=0`;
    }
    
    const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) {
      return `https://player.vimeo.com/video/${vimeoMatch[1]}?autoplay=1&loop=1&autopause=0&muted=${mutedBool}&title=0&byline=0&portrait=0&controls=0`;
    }
    
    return url;
  };

  // 콘텐츠가 바뀌면 음소거 상태 초기화
  useEffect(() => {
    setIsMuted(true);
  }, [currentIndex]);

  if (loading) {
    return (
      <main className="fixed inset-0 bg-gray-900 flex items-center justify-center" role="main">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-coral-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-white text-xl">로딩 중...</p>
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <main className="fixed inset-0 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center" role="main">
        <div className="text-center px-6 max-w-md">
          <div className="w-24 h-24 mx-auto mb-6 flex items-center justify-center bg-red-500/10 rounded-full">
            <i className="ri-wifi-off-line text-5xl text-red-400"></i>
          </div>
          <h2 className="text-3xl font-bold text-white mb-4">
            연결 오류
          </h2>
          <p className="text-gray-400 mb-8 text-lg">
            {error}
          </p>
          <button
            onClick={() => {
              setLoading(true);
              setError(null);
              fetchCelebrations();
            }}
            className="px-8 py-3 bg-gradient-to-r from-coral-500 to-golden-500 text-white rounded-lg hover:shadow-lg hover:scale-105 transition-all whitespace-nowrap text-lg font-semibold cursor-pointer"
          >
            <i className="ri-refresh-line mr-2"></i>
            다시 시도
          </button>
        </div>
      </main>
    );
  }

  if (celebrations.length === 0) {
    return (
      <main className="fixed inset-0 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center" role="main">
        <div className="text-center px-6">
          <div className="w-24 h-24 mx-auto mb-6 flex items-center justify-center bg-white/10 rounded-full">
            <i className="ri-inbox-line text-5xl text-gray-400"></i>
          </div>
          <h2 className="text-3xl font-bold text-white mb-4">
            전시 중인 작품이 없습니다
          </h2>
          <p className="text-gray-400 text-lg">
            작품이 등록되면 자동으로 전시됩니다
          </p>
        </div>
        {/* SEO Hidden Content */}
        <div className="sr-only">
          <h1>미술품 전시 & 판매 플랫폼 - 온라인 아트 갤러리</h1>
          <p>온라인 미술품 전시 및 판매 플랫폼에서 다양한 작가의 회화, 조각, 사진 등 예술 작품을 감상하고 구매하세요. 미술품 전시 판매 플랫폼은 국내외 작가들의 독창적인 예술 작품을 온라인으로 전시하고 판매하는 프리미엄 아트 갤러리 서비스입니다.</p>
        </div>
      </main>
    );
  }

  const currentCelebration = celebrations[currentIndex];
  const mediaUrls = getMediaUrls(currentCelebration);
  const currentMediaUrl = mediaUrls[currentMediaIndex] || currentCelebration.media_url;
  const totalImages = mediaUrls.length;

  // 작품 정보 추출
  const artworkInfo = currentCelebration ? {
    artist_name: currentCelebration.artist_name || '',
    artwork_name: currentCelebration.artwork_name || currentCelebration.title || '',
    artwork_spec: currentCelebration.artwork_spec || '',
    artwork_description: currentCelebration.artwork_description || '',
    artist_description: currentCelebration.artist_description || '',
    artwork_price: currentCelebration.artwork_price || '비공개',
    purchase_location: currentCelebration.purchase_location || '',
  } : null;

  return (
    <main className="fixed inset-0 bg-gray-900 overflow-hidden" role="main">
      {/* SEO: Hidden semantic content for search engines */}
      <div className="sr-only">
        <h1>미술품 전시 & 판매 플랫폼 | 온라인 아트 갤러리</h1>
        <article>
          <h2>미술품 전시 판매 서비스 안내</h2>
          <p>
            <strong>미술품 전시 판매 플랫폼</strong>에서 다양한 장르의 예술 작품을 감상하고 구매하세요. 
            온라인 아트 갤러리는 회화, 조각, 사진, 판화 등 다양한 미술 작품을 
            온라인으로 전시하고 판매하는 프리미엄 미술품 거래 플랫폼입니다.
          </p>
          <section>
            <h3>미술품 판매 신청 방법</h3>
            <p>
              미술품 판매 신청은 간단합니다. 작품 판매 문의 페이지에서 작가명, 작품명, 작품 스펙, 
              희망 판매가 등을 입력하고 작품 이미지를 등록하면 전시 및 판매가 가능합니다. 
              전문 큐레이터가 작품을 검토한 후 온라인 갤러리에 전시됩니다.
            </p>
          </section>
          <section>
            <h3>미술품 전시 판매 플랫폼 특징</h3>
            <ul>
              <li>실시간 온라인 갤러리 전시</li>
              <li>회화, 조각, 사진, 판화 등 다양한 장르 지원</li>
              <li>작품 구매 문의 및 상담 서비스</li>
              <li>작가 프로필 및 작품 상세 정보 제공</li>
            </ul>
          </section>
        </article>
      </div>

      {/* 콘텐츠 표시 영역 */}
      <section className="absolute inset-0 flex items-center justify-center" aria-label="미술품 전시 디스플레이">
        <div
          className={`w-full h-full transition-opacity duration-300 ${fadeIn ? 'opacity-100' : 'opacity-0'}`}
        >
          {currentCelebration.media_type === 'video' ? (
            <div className="relative w-full h-full bg-black">
              <iframe
                key={`${currentCelebration.id}-${isMuted ? 'muted' : 'unmuted'}`}
                src={getEmbedUrl(currentMediaUrl, isMuted)}
                className="w-full h-full"
                allow="autoplay; fullscreen; picture-in-picture; clipboard-write; accelerometer; gyroscope"
                allowFullScreen
                style={{ 
                  border: 'none',
                  objectFit: 'cover',
                  objectPosition: 'center'
                }}
                title={`${currentCelebration.title} - 미술품 전시 작품`}
              />
              {/* 마우스 호버 시 YouTube 채널명/제목 노출 방지 오버레이 */}
              <div 
                className="absolute inset-0 bg-transparent" 
                style={{ pointerEvents: 'auto' }}
                onMouseMove={(e) => e.preventDefault()}
              />
            </div>
          ) : (
            <img
              key={`${currentCelebration.id}-${currentMediaIndex}`}
              src={currentMediaUrl}
              alt={`${currentCelebration.title} - 미술품 전시 작품`}
              title={`${currentCelebration.title} - 온라인 아트 갤러리`}
              className="w-full h-full object-contain"
            />
          )}
        </div>
        
        {/* 오버레이 */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/30 pointer-events-none"></div>

        {/* 동영상 음소거 토글 버튼 */}
        {currentCelebration.media_type === 'video' && (
          <button
            onClick={() => setIsMuted((prev) => !prev)}
            className="absolute bottom-6 left-20 md:bottom-10 md:left-28 z-50 w-12 h-12 md:w-14 md:h-14 flex items-center justify-center bg-black/50 backdrop-blur-md text-white rounded-full hover:bg-black/70 transition-all border border-white/20 cursor-pointer group"
            title={isMuted ? '소리 켜기' : '소리 끄기'}
            aria-label={isMuted ? '소리 켜기' : '소리 끄기'}
          >
            <i className={`${isMuted ? 'ri-volume-mute-line' : 'ri-volume-up-line'} text-xl md:text-2xl`}></i>
            <span className="absolute left-full ml-3 px-3 py-1.5 bg-black/90 text-white text-xs rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none shadow-lg">
              {isMuted ? '소리 켜기' : '소리 끄기'}
            </span>
          </button>
        )}

        {/* 이미지 인디케이터 (여러 이미지일 때만 표시) */}
        {totalImages > 1 && currentCelebration.media_type === 'image' && (
          <div className="absolute bottom-24 md:bottom-28 left-1/2 -translate-x-1/2 flex items-center gap-2 z-20">
            {mediaUrls.map((_, idx) => (
              <div
                key={idx}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  idx === currentMediaIndex
                    ? 'bg-white w-6'
                    : 'bg-white/40'
                }`}
              />
            ))}
          </div>
        )}
      </section>

      {/* 좌측 하단 작품 정보 아이콘 */}
      {celebrations.length > 0 && (
        <button
          onClick={() => setShowArtworkInfo(true)}
          className="fixed bottom-4 left-4 md:bottom-8 md:left-8 w-10 h-10 md:w-10 md:h-10 bg-gradient-to-r from-golden-500 to-coral-500 rounded-full shadow-2xl hover:shadow-golden-500/50 hover:scale-110 transition-all flex items-center justify-center cursor-pointer group"
          style={{ zIndex: 9999 }}
          title="작품 정보 보기"
          aria-label="작품 정보 보기"
        >
          <i className="ri-gallery-line text-lg md:text-lg text-white"></i>
          <span className="absolute left-full ml-4 px-4 py-2 bg-black/90 text-white text-sm rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none shadow-lg">
            작품 정보
          </span>
        </button>
      )}

      {/* 작품 정보 팝업 모달 */}
      {showArtworkInfo && artworkInfo && (
        <div 
          className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => setShowArtworkInfo(false)}
        >
          <div 
            className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* 헤더 */}
            <div className="sticky top-0 bg-gradient-to-r from-coral-500 to-golden-500 text-white px-6 py-4 flex items-center justify-between rounded-t-2xl">
              <h2 className="text-2xl font-bold">작품 정보</h2>
              <button
                onClick={() => setShowArtworkInfo(false)}
                className="w-8 h-8 flex items-center justify-center hover:bg-white/20 rounded-full transition-colors cursor-pointer"
                aria-label="닫기"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            {/* 콘텐츠 */}
            <div className="p-6 space-y-6">
              {/* 작품명 */}
              {artworkInfo.artwork_name && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-500 mb-2">작품명</h3>
                  <p className="text-xl font-bold text-gray-900">{artworkInfo.artwork_name}</p>
                </div>
              )}

              {/* 작가명 */}
              {artworkInfo.artist_name && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-500 mb-2">작가명</h3>
                  <p className="text-lg text-gray-800">{artworkInfo.artist_name}</p>
                </div>
              )}

              {/* 작품 스펙 */}
              {artworkInfo.artwork_spec && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-500 mb-2">작품 스펙</h3>
                  <p className="text-base text-gray-700 whitespace-pre-wrap">{artworkInfo.artwork_spec}</p>
                </div>
              )}

              {/* 작품 설명 */}
              {artworkInfo.artwork_description && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-500 mb-2">작품 설명</h3>
                  <p className="text-base text-gray-700 whitespace-pre-wrap leading-relaxed">{artworkInfo.artwork_description}</p>
                </div>
              )}

              {/* 작가 설명 */}
              {artworkInfo.artist_description && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-500 mb-2">작가 설명</h3>
                  <p className="text-base text-gray-700 whitespace-pre-wrap leading-relaxed">{artworkInfo.artist_description}</p>
                </div>
              )}

              {/* 작품 가격 */}
              <div>
                <h3 className="text-sm font-semibold text-gray-500 mb-2">작품 가격</h3>
                <p className="text-lg font-bold text-coral-600">
                  {(() => {
                    const price = artworkInfo.artwork_price;
                    if (!price || price === '비공개') return '비공개';
                    const num = typeof price === 'number' ? price : Number(String(price).replace(/[^0-9]/g, ''));
                    if (!isNaN(num) && String(price).replace(/[^0-9]/g, '') !== '') {
                      return `${num.toLocaleString('ko-KR')}원`;
                    }
                    return price;
                  })()}
                </p>
              </div>

              {/* 구매처 */}
              {artworkInfo.purchase_location && (
                <div>
                  <h3 className="text-sm font-semibold text-gray-500 mb-2">구매처</h3>
                  <p className="text-base text-gray-700">{artworkInfo.purchase_location}</p>
                </div>
              )}

              {/* 구매 문의 버튼 */}
              <div className="pt-4 border-t border-gray-200">
                <button
                  onClick={() => {
                    setShowArtworkInfo(false);
                    window.REACT_APP_NAVIGATE('/buy-inquiry');
                  }}
                  className="w-full py-4 bg-gradient-to-r from-coral-500 to-golden-500 text-white rounded-xl font-semibold text-lg hover:shadow-lg hover:scale-[1.02] transition-all whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-shopping-cart-line mr-2"></i>
                  작품 구매 문의하기
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 작품 판매 문의 플로팅 버튼 */}
      <a
        href="/sell-inquiry"
        onClick={(e) => { e.preventDefault(); window.REACT_APP_NAVIGATE('/sell-inquiry'); }}
        className="fixed bottom-4 right-4 md:bottom-8 md:right-8 w-10 h-10 md:w-10 md:h-10 bg-gradient-to-r from-coral-500 to-golden-500 rounded-full shadow-2xl hover:shadow-coral-500/50 hover:scale-110 transition-all flex items-center justify-center cursor-pointer group"
        style={{ zIndex: 9999 }}
        title="작품 판매 문의하기"
        aria-label="작품 판매 문의하기"
      >
        <i className="ri-palette-line text-lg md:text-lg text-white"></i>
        <span className="absolute right-full mr-4 px-4 py-2 bg-black/90 text-white text-sm rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none shadow-lg">
          작품 판매 문의
        </span>
      </a>

      {/* Footer with Readdy link */}
      <footer className="fixed bottom-1 left-1/2 -translate-x-1/2 z-40">
        <a href="https://readdy.ai/?ref=logo" rel="nofollow" className="text-white/20 hover:text-white/40 text-[10px] transition-colors">
          Powered by Readdy
        </a>
      </footer>
    </main>
  );
}
